import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiliTextInput } from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { FieldError } from '@Components/inputs/FieldError';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import { CustomRadioButton } from '@Components/inputs/RadioButton/CustomRadioButton';
import { WEBSITE_OPTIONS } from './hooks/useWebsiteSocialLogic';

const AnimatedReveal = ({ show, children }: { show: boolean; children: React.ReactNode }) => (
  <div
    className='grid transition-[grid-template-rows] duration-200 ease-in-out'
    style={{ gridTemplateRows: show ? '1fr' : '0fr' }}>
    <div className='overflow-hidden'>{children}</div>
  </div>
);

const UrlInput = ({ placeholder }: { placeholder: string }) => (
  <ControllerWrapper name={EddEmbeddedFields.EDD_WEBSITE_URL}>
    {({ onChange: onUrlChange, value: urlValue, error, onBlur }) => (
      <div>
        <LiliTextInput
          value={urlValue}
          onChange={onUrlChange}
          errorMessage={error?.message}
          title={placeholder}
          onBlur={onBlur}
          id={EddEmbeddedFields.EDD_WEBSITE_URL}
          isPublic
        />
      </div>
    )}
  </ControllerWrapper>
);

export const WebsiteSocialForm = () => {
  const { t } = useTranslation('eddQuestions');
  const { watch, setValue } = useFormContext();
  const selectedOption = watch(EddEmbeddedFields.EDD_WEBSITE_OPTION);

  useEffect(() => {
    if (selectedOption === WEBSITE_OPTIONS.NO_PUBLIC_SITE) {
      setValue(EddEmbeddedFields.EDD_WEBSITE_URL, '', { shouldValidate: false });
    }
  }, [selectedOption, setValue]);

  const getPlaceholder = () => {
    if (selectedOption === WEBSITE_OPTIONS.ONLINE_PROFILE) {
      return t('organic.websiteSocial.onlineProfilePlaceholder');
    }
    return t('organic.websiteSocial.websitePlaceholder');
  };

  const showUrlUnderWebsite = selectedOption === WEBSITE_OPTIONS.WEBSITE;
  const showUrlUnderProfile = selectedOption === WEBSITE_OPTIONS.ONLINE_PROFILE;

  return (
    <div className='flex flex-col mt-[16px] gap-4'>
      <ControllerWrapper name={EddEmbeddedFields.EDD_WEBSITE_OPTION}>
        {({ onChange, value, error }) => (
          <>
            <div className='flex flex-col gap-2'>
              <CustomRadioButton
                label={t('organic.websiteSocial.optionWebsite')}
                value={WEBSITE_OPTIONS.WEBSITE}
                inputId='website-option'
                onChange={onChange}
                checked={value === WEBSITE_OPTIONS.WEBSITE}
                name={EddEmbeddedFields.EDD_WEBSITE_OPTION}
              />
              <AnimatedReveal show={showUrlUnderWebsite}>
                {showUrlUnderWebsite && <UrlInput placeholder={getPlaceholder()} />}
              </AnimatedReveal>
            </div>

            <div className='flex flex-col gap-2'>
              <CustomRadioButton
                label={t('organic.websiteSocial.optionOnlineProfile')}
                value={WEBSITE_OPTIONS.ONLINE_PROFILE}
                inputId='online-profile-option'
                onChange={onChange}
                checked={value === WEBSITE_OPTIONS.ONLINE_PROFILE}
                name={EddEmbeddedFields.EDD_WEBSITE_OPTION}
              />
              <AnimatedReveal show={showUrlUnderProfile}>
                {showUrlUnderProfile && <UrlInput placeholder={getPlaceholder()} />}
              </AnimatedReveal>
            </div>

            <CustomRadioButton
              label={t('organic.websiteSocial.optionNoSite')}
              value={WEBSITE_OPTIONS.NO_PUBLIC_SITE}
              inputId='no-site-option'
              onChange={onChange}
              checked={value === WEBSITE_OPTIONS.NO_PUBLIC_SITE}
              name={EddEmbeddedFields.EDD_WEBSITE_OPTION}
            />
            <FieldError error={error} testId={`${EddEmbeddedFields.EDD_WEBSITE_OPTION}-error`} />
          </>
        )}
      </ControllerWrapper>
    </div>
  );
};
